import t from 'prop-types';
import { omit } from 'ramda';
import React, { useState } from 'react';
import ReactInputMask from 'react-input-mask';
import styled from 'styled-components';

import { validatePassword } from 'utils/helpers';
import { precision } from 'utils/numberUtils';

const Wrapper = styled.div`
  display: inline-block;
  width: 100%;
`;

const StyledInput = styled(ReactInputMask)`
  width: 100%;
  background-color: #ffffff;
  border: 1px solid #dedede;
  border-radius: 2px;
  outline: none;
  margin: 0px;
  padding: ${(props) => {
    switch (props.size) {
      case 'small':
        return '0.5em 0.5em';
      default:
      case 'medium':
        return '0.7em 0.65em';
      case 'large':
        return '1em 0.85em';
    }
  }};

  font-family: inherit;
  font-size: ${(props) => props.scale}px;

  &::placeholder {
    color: #aaaaaa;
  }

  &:hover:not([disabled]) {
    border-color: #aaaaaa;
  }

  &:focus:not([disabled]) {
    border-color: #828282;
  }

  &[disabled] {
    background-color: #f4f4f4;
    cursor: not-allowed;

    ${(props) =>
      props.error &&
      `
      border-color: #D33131;
    `};
  }

  ${(props) =>
    props.error &&
    `
    &, &:hover:not([disabled]), &:focus:not([disabled]) {
      border-color: #D33131;
      background-color: rgba(255, 91, 93, 0.02);
    }
  `};
`;

const Message = styled.div`
  color: #666;
  font-size: 12px;
  margin-top: 2px;
  ${(props) =>
    props.error &&
    `
    color: #D33131;
  `};
  ${(props) =>
    props.valid &&
    `
    color: #398454;
  `}
`;

const PasswordErrorsWrapper = styled.div`
  float: left;
  > {
    display: block;
  }
`;

const PasswordErrors = ({ password, showErrors }) => {
  const errors = validatePassword(password);
  if (!errors || !showErrors) return null;
  const bullet = '\u2022 ';
  return (
    <PasswordErrorsWrapper>
      <Message error={errors['minLength']} valid={!errors['minLength']}>
        {bullet}Use 15 or more characters
      </Message>
    </PasswordErrorsWrapper>
  );
};

const ConfirmPasswordErrors = ({ showErrors }) => {
  if (!showErrors) return null;
  const bullet = '\u2022 ';
  return (
    <PasswordErrorsWrapper>
      <Message error={showErrors} valid={!showErrors}>
        {bullet}Passwords Must Match
      </Message>
    </PasswordErrorsWrapper>
  );
};

class TextInput extends React.Component {
  input = null;

  constructor(props) {
    super(props);
    const { value, defaultValue } = props;
    this.state = {
      value:
        value !== undefined
          ? value
          : defaultValue !== undefined
          ? defaultValue
          : '',
    };
  }

  static propTypes = {
    autoFocus: t.bool,
    size: t.string,
    className: t.string,
    inputClassName: t.string,
    error: t.oneOfType([t.string, t.bool]),
    hint: t.string,
    isValueAllowed: t.func,
  };

  static defaultProps = {
    autoFocus: false,
    scale: 14,
    type: 'text',
    isValueAllowed: () => true,
  };

  componentDidMount() {
    if (this.props.autoFocus) {
      setTimeout(() => this.focus(), 0);
    }
  }

  static getDerivedStateFromProps(props) {
    if (props.value !== undefined) {
      return { value: props.value };
    }
    return null;
  }

  focus() {
    this.input.focus();
  }

  blur() {
    this.input.blur();
  }
  render() {
    const {
      autoComplete,
      className,
      inputClassName,
      error,
      hint,
      size,
      scale,
      isValueAllowed,
      ...rest
    } = this.props;
    const { value } = this.state;
    const props = omit(['defaultValue', 'autoFocus', 'hasError'], rest);

    return (
      <Wrapper className={className}>
        <StyledInput
          className={inputClassName}
          ref={(ref) => {
            if (ref) {
              this.input = ref.input;
            }
          }}
          error={error}
          size={size}
          scale={scale}
          {...props}
          autoComplete={autoComplete === 'none' ? 'off' : autoComplete}
          value={value}
          onChange={(e) => {
            const { value } = e.target;
            if (isValueAllowed(value)) {
              this.props.onChange(e);
              this.setState({ value });
            }
          }}
          onWheel={() => this.input.blur()}
        />
        {hint && <Message>{hint}</Message>}
        {error && typeof error === 'string' && (
          <Message error role="alert">
            {error}
          </Message>
        )}
      </Wrapper>
    );
  }
}

/**
 * @deprecated use Input from '@frontend/legacy-components' instead.
 */
const PhoneInput = styled((props) => {
  return (
    <TextInput
      {...props}
      mask="(999) 999-9999"
      maskChar={null}
      onChange={(e) => {
        e.target.value = e.target.value.replace(/[^0-9]/g, '');
        props.onChange(e);
      }}
    />
  );
})``;

/**
 * @deprecated use NumberInput from '@frontend/legacy-components' instead.
 */
class FloatInput extends React.Component {
  render() {
    return (
      <TextInput
        {...this.props}
        isValueAllowed={(value) => {
          let isAllowed =
            value === '' ||
            value === '-' ||
            value === '.' ||
            !isNaN(Number(value));
          if (isAllowed && !isNaN(Number(value)) && this.props.decimalPlaces) {
            const decimalPlaces = precision(value);
            if (decimalPlaces > this.props.decimalPlaces) isAllowed = false;
          }
          return isAllowed;
        }}
      />
    );
  }
}

/**
 * @deprecated use NumberInput from '@frontend/legacy-components' instead.
 */
class IntegerInput extends React.Component {
  render() {
    return (
      <TextInput
        {...this.props}
        isValueAllowed={(value) => {
          return (
            value === '' || (!isNaN(Number(value)) && /^[0-9]+$/.test(value))
          );
        }}
      />
    );
  }
}

/**
 * @deprecated use PasswordInput from '@frontend/legacy-components' instead.
 */
const PasswordInput = (props) => {
  return (
    <Wrapper>
      <TextInput {...props} />
      <PasswordErrors password={props.value} showErrors={props.hasError} />
    </Wrapper>
  );
};

/**
 * @deprecated use PasswordInput from '@frontend/legacy-components' instead.
 */
const ConfirmPasswordInput = (props) => {
  return (
    <Wrapper>
      <TextInput {...props} />
      <ConfirmPasswordErrors showErrors={props.hasError} />
    </Wrapper>
  );
};

const DecoratorWrapper = styled.div`
  display: flex;
`;

const DecoratorContent = styled.div`
  flex-grow: 1;
  input {
    ${(props) =>
      props.hasAddonBefore &&
      `
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    `} ${(props) =>
      props.hasAddonAfter &&
      `
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    `};
  }
`;

const DecoratorAddon = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  color: rgb(74, 74, 74);
  background-color: #f4f4f4;
  border: 1px solid #dedede;
  border-radius: 2px;
  font-size: 14px;
  font-weight: 600;
  padding: 0 0.75em;
`;

const DecoratorAddonBefore = styled(DecoratorAddon)`
  border-right: 0;
  border-bottom-rigth-radius: 0px;
  border-top-right-radius: 0px;
`;

const DecoratorAddonAfter = styled(DecoratorAddon)`
  border-left: 0;
  border-bottom-left-radius: 0px;
  border-top-left-radius: 0px;
`;

/**
 * @deprecated use Input with InputLeftAddon and InputRightAddon from
 * '@frontend/legacy-components' instead.
 */
const InputDecorator = ({ addonAfter, addonBefore, children }) => {
  return (
    <DecoratorWrapper>
      {addonBefore && (
        <DecoratorAddonBefore>{addonBefore}</DecoratorAddonBefore>
      )}
      <DecoratorContent
        hasAddonBefore={!!addonBefore}
        hasAddonAfter={!!addonAfter}
      >
        {React.Children.only(children)}
      </DecoratorContent>
      {addonAfter && <DecoratorAddonAfter>{addonAfter}</DecoratorAddonAfter>}
    </DecoratorWrapper>
  );
};

// Ugly hack to prevent chrome autofill
// tried all variants of autocomplete, hidden fields, and name/label changes
// chrome seems to find it as long as email is in the name or nearby label
/**
 * @deprecated use Input from '@frontend/legacy-components' instead.
 */
const AutofillBlockingInput = ({
  autoComplete,
  error,
  name,
  onChange,
  id,
  value,
}) => {
  const [readonly, setReadonly] = useState(true);
  const clearReadonly = () => setReadonly(false);

  return (
    <TextInput
      autoComplete={autoComplete}
      id={id}
      error={error}
      name={name}
      onChange={onChange}
      value={value}
      readOnly={readonly}
      onFocus={clearReadonly}
    />
  );
};

export {
  AutofillBlockingInput,
  FloatInput,
  IntegerInput,
  ConfirmPasswordInput,
  PasswordInput,
  PhoneInput,
  InputDecorator,
};

/**
 * @deprecated use Input from '@frontend/legacy-components' instead.
 */
const StyledTextInput = styled(TextInput)``;

export default StyledTextInput;
